import "./styles.scss";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import { useCallback, useState } from "react";
import { useVAdminSelectedIncident } from "#root/queries/local";
import { Incident, useIncidentById, useInfiniteIncidents } from "#root/queries/incidents";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Field, Input, Option, Spinner } from "@fluentui/react-components";
import { getEnumLabelByValue } from "#root/utils/enums";
import { USR_TEAMS, RESPONDER_ROLES_CONST } from "#root/utils/responders";
import LoadingWrapper from "#root/components/LoadingWrapper";
import { useInfiniteResponders } from "#root/queries/responders";
import { CommunicationPersonRegular } from "@fluentui/react-icons";
import useInfiniteScroll from "#root/hooks/useInfiniteScroll";
import { ResponderTravelCard } from "#root/components/ResponderTravelCard";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DismissRegular } from "@fluentui/react-icons";
import { ROUTES } from "#root/routes/constants";
import { formatToLocalTime, handleDateSelection } from '#root/services/utils';

const ITEMS_PER_PAGE = 10;

export interface FilterState {
    startDate?: Date;
    endDate?: Date;
    name?: string;
    departmentOrAgency?: string;
    usarTeamNumber?: number;
    activeTeams?: number;
    role?: number;
}

export const initialFilters: FilterState = {
    startDate: undefined,
    endDate: undefined,
    name: '',
    departmentOrAgency: '',
    usarTeamNumber: undefined,
    activeTeams: undefined,
    role: undefined
};

const TimeAndTravel: React.FC = () => {
    const navigate = useNavigate();
    const { selectedIncidentId: vAdminIncidentId, setVAdminSelectedIncidentId } = useVAdminSelectedIncident();
    const [selectedIncidentId, setSelectedIncidentId] = useState<string | ''>(vAdminIncidentId || '');
    const [filters, setFilters] = useState<FilterState>(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState<FilterState>(initialFilters);

    const {
        selectedData: selectedIncidentData,
        handleChange: handleIncidentChange,
        setFilter: setIncidentFilter,
        queryResult: incidentQueryResult
    } = useInfiniteDropdown<Incident>({
        initialId: selectedIncidentId || '',
        onChange: (id) => {
            setSelectedIncidentId(id || '');
            if (id) {
                setVAdminSelectedIncidentId(id);
            }
        },
        useInfiniteQuery: useInfiniteIncidents,
        useItemById: useIncidentById
    });

    const {
        data: respondersData,
        isLoading,
        isFetching,
        refetch,
        fetchNextPage,
        hasNextPage
    }
        = useInfiniteResponders
            ({
                incidentId: selectedIncidentId || '',
                take: ITEMS_PER_PAGE,
                includeTimeLogs: true,
                ...appliedFilters
            });

    const responders = respondersData?.pages.flatMap(page => page.items) ?? [];

    const listRef = useInfiniteScroll({
        isLoading,
        hasNextPage,
        onLoadMore: () => fetchNextPage()
    });

    const handleFilterChange = useCallback((field: keyof FilterState, value: any) => {
        // Only update local filters, don't trigger API call
        setFilters(prev => ({
            ...prev,
            [field]: value === '' ? undefined : value
        }));
    }, []);

    const handleSearch = () => {
        // Apply the current filters to the query
        setAppliedFilters(filters);
        refetch();
    };
    
    const formatDate = (value?: Date | string): string => {
        if (!value) return "";
        return formatToLocalTime(value, 'date', false);
    };

    return (
        <div className="time-and-travel-container layout__body--paddings">
            <div className="time-and-travel">
                <div className="emc-page-header">
                    <div className="emc-page-header__top-row">
                        <div className="emc-page-header__breadcrumbs">
                            <Breadcrumbs />
                        </div>
                    </div>
                    <div className="emc-page-header__title">
                        <h2>Time and Travel <div className="divider" />
                            <Field label="Incident" className="all-victims__incident">
                                <InfiniteDropdown
                                    value={selectedIncidentId || ''}
                                    onChange={(_, data) => handleIncidentChange(data.value)}
                                    onFilterChange={setIncidentFilter}
                                    queryResult={incidentQueryResult}
                                    itemToOption={(incident) => ({
                                        value: incident.id ?? '',
                                        text: incident.name ?? ''
                                    })}
                                    placeholder="All Incidents"
                                    selectedItem={selectedIncidentData}
                                    clearable
                                />
                            </Field>
                        </h2>
                    </div>
                </div>

                <div className="time-and-travel__filters">
                    <Field label="Start Date" className='time-and-travel__filter'>
                        <DatePicker
                            name="startDate"
                            value={filters.startDate ? new Date(filters.startDate) : undefined}
                            onSelectDate={(date) => handleDateSelection("startDate", date, (name, value) => handleFilterChange(name as keyof FilterState, value))}
                            formatDate={formatDate}
                            className="date-picker"
                            allowTextInput
                            contentAfter={filters.startDate ? 
                                <Button 
                                    icon={<DismissRegular />} 
                                    appearance="transparent" 
                                    size="small"
                                    onClick={() => handleFilterChange('startDate', undefined)}
                                    aria-label="Clear date"
                                /> : undefined
                            }
                        />
                    </Field>
                    <Field label="End Date" className='time-and-travel__filter'>
                        <DatePicker
                            name="endDate"
                            value={filters.endDate ? new Date(filters.endDate) : undefined}
                            onSelectDate={(date) => handleDateSelection("endDate", date, (name, value) => handleFilterChange(name as keyof FilterState, value))}
                            formatDate={formatDate}
                            className="date-picker"
                            allowTextInput
                            contentAfter={filters.endDate ? 
                                <Button 
                                    icon={<DismissRegular />} 
                                    appearance="transparent" 
                                    size="small"
                                    onClick={() => handleFilterChange('endDate', undefined)}
                                    aria-label="Clear date"
                                /> : undefined
                            }
                        />
                    </Field>
                    <Field label="Name" className='time-and-travel__filter'>
                        <Input
                            value={filters.name || ''}
                            onChange={(_, data) => handleFilterChange('name', data.value)}
                        />
                    </Field>
                    <Field label="Department/Agency" className='time-and-travel__filter'>
                        <Input
                            value={filters.departmentOrAgency || ''}
                            onChange={(_, data) => handleFilterChange('departmentOrAgency', data.value)}
                        />
                    </Field>
                    <Field label="US&R Team" className='time-and-travel__filter usar'>
                        <Dropdown
                            clearable
                            value={filters.usarTeamNumber !== undefined ? getEnumLabelByValue(USR_TEAMS, filters.usarTeamNumber) : ''}
                            onOptionSelect={(_, data) => handleFilterChange('usarTeamNumber', data.optionValue ? Number(data.optionValue) : undefined)}
                        >
                            {USR_TEAMS.map(team => (
                                <Option key={team.value} value={String(team.value)}>
                                    {team.label}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>
                    <Field label="Active Teams" className='time-and-travel__filter active-teams'>
                        <Dropdown
                            clearable
                            value={filters.activeTeams !== undefined ? String(filters.activeTeams) : undefined}
                            onOptionSelect={(_, data) => handleFilterChange('activeTeams', data.optionValue ? Number(data.optionValue) : undefined)}
                        >
                            <Option key={1} value={String(1)}>
                                Active Team
                            </Option>
                        </Dropdown>
                    </Field>
                    <Field label="Role" className='time-and-travel__filter role'>
                        <Dropdown
                            clearable
                            value={filters.role !== undefined ? getEnumLabelByValue(RESPONDER_ROLES_CONST, filters.role) : ''}
                            onOptionSelect={(_, data) => handleFilterChange('role', data.optionValue ? Number(data.optionValue) : undefined)}
                        >
                            {RESPONDER_ROLES_CONST.map(role => (
                                <Option key={role.value} value={String(role.value)}>
                                    {role.label}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>
                    <Button
                        appearance="primary"
                        onClick={() => handleSearch()}
                    >
                        Search
                    </Button>
                </div>

                <div className="time-and-travel__content">
                    <LoadingWrapper
                        isLoading={isLoading}
                        isEmpty={!isLoading && responders.length === 0}
                        emptyMessage={`No responders matching these filters found.`}
                        emptyIcon={<CommunicationPersonRegular />}
                    >
                        <div ref={listRef} className="tab-content">
                            {responders.map(responder => {
                                return (
                                    <ResponderTravelCard
                                        key={responder.id!}
                                        responder={responder}
                                        onClick={() => navigate(`${ROUTES.MERRGE211.TIME_AND_TRAVEL_DETAILS}?responderId=${responder.id}`)}
                                    />
                                );
                            })}
                            {isFetching && (
                                <div className="time-and-travel__column-loading">
                                    <Spinner size="tiny" />
                                </div>
                            )}
                        </div>
                    </LoadingWrapper>
                </div>
            </div>
        </div>
    );
};

export default TimeAndTravel;